import { useState, useEffect } from 'react';
import dataService from '../services/dataService';

const useApp = (categoryId = null, navigate = null) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [songs, setSongs] = useState([]);
  const [allSongs, setAllSongs] = useState([]); // Store all songs for pagination
  const [categorySongCounts, setCategorySongCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [songsPerPage, setSongsPerPage] = useState(10);

  // Determine active category from URL or default - this will update when categoryId changes
  const activeCategory = categoryId || 'home';

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Load songs when category changes
  useEffect(() => {
    if (activeCategory && activeCategory !== 'home') {
      loadSongs(activeCategory);
      // Reset to page 1 when category changes
      setCurrentPage(1);
    } else {
      setSongs([]);
      setAllSongs([]);
    }
  }, [activeCategory]);

  // Load pagination settings and restore page from local storage
  useEffect(() => {
    if (config?.ui?.songsPerPage) {
      setSongsPerPage(config.ui.songsPerPage);
    }
    
    // Restore current page from local storage
    const savedPage = localStorage.getItem(`currentPage_${activeCategory}`);
    if (savedPage && activeCategory !== 'home') {
      setCurrentPage(parseInt(savedPage, 10));
    }
  }, [config, activeCategory]);

  // Save current page to local storage when it changes
  useEffect(() => {
    if (activeCategory && activeCategory !== 'home') {
      localStorage.setItem(`currentPage_${activeCategory}`, currentPage.toString());
    }
  }, [currentPage, activeCategory]);

  // Update displayed songs when page or songs per page changes
  useEffect(() => {
    if (allSongs.length > 0) {
      const startIndex = (currentPage - 1) * songsPerPage;
      const endIndex = startIndex + songsPerPage;
      const paginatedSongs = allSongs.slice(startIndex, endIndex);
      setSongs(paginatedSongs);
    }
  }, [currentPage, songsPerPage, allSongs]);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim()) {
      handleSearch(searchQuery);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesData, configData] = await Promise.all([
        dataService.loadCategories(),
        dataService.loadConfig()
      ]);
      
      setCategories(categoriesData);
      setConfig(configData);
      
      // Load song counts for all categories
      await loadCategorySongCounts(categoriesData);
      
      // Check if we need to navigate to default category
      if (!categoryId && navigate) {
        const defaultCategory = configData.ui?.defaultCategory || 'home';
        if (defaultCategory !== 'home') {
          navigate(`/category/${defaultCategory}`);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategorySongCounts = async (categoriesData) => {
    const counts = {};
    
    for (const category of categoriesData) {
      if (category.songFile) {
        try {
          const categorySongs = await dataService.loadSongs(category.id);
          counts[category.id] = categorySongs.length;
        } catch (error) {
          console.error(`Error loading songs for category ${category.id}:`, error);
          counts[category.id] = 0;
        }
      } else {
        counts[category.id] = 0;
      }
    }
    
    setCategorySongCounts(counts);
  };

  const loadSongs = async (categoryId) => {
    try {
      console.log(`useApp: Loading songs for ${categoryId}`);
      const songsData = await dataService.loadSongs(categoryId);
      
      // Filter out songs without song numbers
      const filteredSongs = songsData.filter(song => 
        song.songNumber && song.songNumber.trim() !== ''
      );
      
      console.log(`useApp: Filtered ${songsData.length} songs to ${filteredSongs.length} songs with valid numbers`);
      
      // Store all songs for pagination
      setAllSongs(filteredSongs);
      
      // Set initial page songs (this will be updated by pagination useEffect)
      const startIndex = (currentPage - 1) * songsPerPage;
      const endIndex = startIndex + songsPerPage;
      const paginatedSongs = filteredSongs.slice(startIndex, endIndex);
      setSongs(paginatedSongs);
      
      console.log(`useApp: Songs state updated with ${paginatedSongs.length} songs for page ${currentPage}`);
    } catch (error) {
      console.error('Error loading songs:', error);
      setSongs([]);
      setAllSongs([]);
    }
  };

  const handleSearch = async (query) => {
    try {
      const allSongs = await dataService.getAllSongs();
      const searchTerm = query.toLowerCase().trim();
      
      const results = allSongs.filter(song => 
        song.title.toLowerCase().includes(searchTerm) ||
        (song.englishTitle && song.englishTitle.toLowerCase().includes(searchTerm)) ||
        song.artist.toLowerCase().includes(searchTerm) ||
        song.songNumber === searchTerm ||
        song.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );
      
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching songs:', error);
      setSearchResults([]);
    }
  };

  const handleSongClick = (song) => {
    const url = config?.youtube?.openInNewTab 
      ? song.youtubeUrl 
      : song.embedUrl;
    
    if (config?.youtube?.openInNewTab) {
      window.open(url, '_blank');
    } else {
      window.open(url, '_blank');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getTotalPages = () => {
    return Math.ceil(allSongs.length / songsPerPage);
  };

  const getPaginationInfo = () => {
    const totalPages = getTotalPages();
    const startItem = allSongs.length === 0 ? 0 : (currentPage - 1) * songsPerPage + 1;
    const endItem = Math.min(currentPage * songsPerPage, allSongs.length);
    return { startItem, endItem, totalPages, totalItems: allSongs.length };
  };

  const currentCategory = categories.find(cat => cat.id === activeCategory);

  // Debug: Log current songs state
  // Removed debug logs

  return {
    // State
    isSidebarOpen,
    categories,
    songs,
    allSongs,
    categorySongCounts,
    loading,
    config,
    searchQuery,
    searchResults,
    currentPage,
    songsPerPage,
    
    // Actions
    setIsSidebarOpen,
    onSongClick: handleSongClick,
    setSearchQuery,
    setSearchResults,
    onPageChange: handlePageChange,
    
    // Computed
    currentCategory,
    paginationInfo: getPaginationInfo(),
    totalPages: getTotalPages(),
  };
};

export default useApp;
